import { createContext, useContext, useEffect, useMemo, useState } from "react";

// LocalStorage key for the "saved designs" mini-cart. We keep this purely client-side
// so unauthenticated retail visitors can build up a wishlist without signing in.
const STORAGE_KEY = "sv_saved_designs_v1";

const SavedDesignsContext = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function SavedDesignsProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  // Mirror state to localStorage so the wishlist survives reloads and tab swaps.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* quota */ }
  }, [items]);

  // Lightweight de-dupe: cards are stored by id, never duplicated.
  const value = useMemo(() => ({
    items,
    count: items.length,
    isSaved: (id) => items.some((x) => x.id === id),
    toggle: (card) => {
      setItems((prev) => {
        if (prev.some((x) => x.id === card.id)) {
          return prev.filter((x) => x.id !== card.id);
        }
        // Store ONLY the fields we need to render the saved page + WhatsApp message,
        // so the LS payload stays tiny even with 100+ saved designs.
        return [
          ...prev,
          {
            id: card.id,
            code: card.code || null,
            name: card.name || card.code || "Design",
            price: card.price ?? null,
            image_path: card.image_path || null,
            series_id: card.series_id || null,
            series_name: card.series_name || null,
            available: card.available !== false,
            saved_at: new Date().toISOString(),
          },
        ];
      });
    },
    remove: (id) => setItems((prev) => prev.filter((x) => x.id !== id)),
    clear: () => setItems([]),
  }), [items]);

  return <SavedDesignsContext.Provider value={value}>{children}</SavedDesignsContext.Provider>;
}

export function useSavedDesigns() {
  const ctx = useContext(SavedDesignsContext);
  if (!ctx) throw new Error("useSavedDesigns must be used inside <SavedDesignsProvider>");
  return ctx;
}
